import { ProctoringAssessmentConfigurationV2, ProctoringOptionProfileV2 } from "@verity/proctoring-option/types";
import ConfirmationBlock, {
	CommonConfirmationBlockProps,
	ConfirmationBlockSkeleton,
	ConfirmationRow,
	ConfirmationRowDescription,
} from "./ConfirmationBlock";
import i18nPackage from "../../../i18n/i18n";
import {
	getDisplayedPlatformsFromProctoringOptions,
	getPlatformTextFromProctoringOptions,
	getProctoringMethodNameFromConfiguration,
	getProctoringPlatformName,
} from "../../proctoring-options/option-text/ProctoringOptionText";
import { getProfileName } from "../../profile/profileHelpers";
import ConfigurationSummaryList, { ConfigurationSummaryListProps, DropdownRow } from "../ConfigurationSummaryList";
import { useMemo, useRef } from "react";
import { categorizeProctoringOptions, CategorizeProctoringOptionsParams } from "../confirmationHelper";
import { EditQuizSummaryProps } from "../EditQuizSummary";
import { CorePopoverButton } from "../../../core";
import MobileIcon from "../../images/confirmation/mobile.svg?react";
import ManagedGuestSessionIcon from "../../images/confirmation/managed-guest-session.svg?react";
import PersonalChromebookIcon from "../../images/confirmation/personal-chromebook.svg?react";
import { List } from "@mui/material";
import { SUPPORTED_BROWSER_CONFIG, SUPPORTED_OS_CONFIG } from "../../edit-quiz-steps/StepIconConfig";

type MethodBlockProps = {
	proctoringAssessmentConfiguration?: Omit<Partial<ProctoringAssessmentConfigurationV2>, "proctoringOptionProfile">;
} & CommonConfirmationBlockProps;

export function MethodBlock({ proctoringAssessmentConfiguration, loading, ...props }: MethodBlockProps) {
	if (loading || !proctoringAssessmentConfiguration) {
		return <ConfirmationBlockSkeleton />;
	}

	const methodText = getProctoringMethodNameFromConfiguration(proctoringAssessmentConfiguration);

	return (
		<ConfirmationBlock
			{...props}
			targetPageName="method"
			title={i18nPackage.t("editQuizPage.confirmationBlockTSX.method")}
		>
			<ConfirmationRow description={methodText} titleTooltipType="wrap" />
		</ConfirmationBlock>
	);
}

type PlatformOptionsInput = Parameters<typeof getPlatformTextFromProctoringOptions>[0];

export type PlatformBlockProps = CommonConfirmationBlockProps & {
	platformOptions?: PlatformOptionsInput;
} & Pick<EditQuizSummaryProps, "optionRelatedInstituteSettings">;

export function PlatformBlock({
	loading,
	platformOptions,
	optionRelatedInstituteSettings,
	...props
}: PlatformBlockProps) {
	const anchorRef = useRef<HTMLDivElement | null>(null);
	if (loading || !platformOptions) return <ConfirmationBlockSkeleton />;

	const platforms = getDisplayedPlatformsFromProctoringOptions(platformOptions);

	const isExtension = platforms.includes("extension");
	const isBrowser = platforms.includes("browser");
	const isMobile = platforms.includes("mobile");
	const isChromeOs = platforms.includes("chromeOs");
	const primaryPlatform = isExtension ? "extension" : isBrowser ? "browser" : "lockdown";
	// The browser platform runs in the student's own browser, so its support list is the browser list
	const supportListsBrowsers = isExtension || isBrowser;

	const lockdownRequireManagedGuestSession =
		optionRelatedInstituteSettings?.lockdownRequireManagedGuestSession ?? false;

	const configs = Object.values(supportListsBrowsers ? SUPPORTED_BROWSER_CONFIG : SUPPORTED_OS_CONFIG);

	return (
		<ConfirmationBlock
			{...props}
			targetPageName="platform"
			title={i18nPackage.t("editQuizPage.confirmationBlockTSX.platform")}
			ref={anchorRef}
		>
			<ConfirmationRow
				title={
					<>
						<CorePopoverButton
							title={i18nPackage.t("editQuizPage.confirmationBlockTSX.additionalPlatforms")}
							anchorEl={anchorRef.current}
							variant="text"
							buttonText={
								<ConfirmationRowDescription tooltipType="wrap">
									{i18nPackage.t("editQuizPage.confirmationBlockTSX.platformRequired", {
										platform: getProctoringPlatformName(primaryPlatform, true),
									})}
								</ConfirmationRowDescription>
							}
							maxWidth={250}
							tooltip={
								supportListsBrowsers
									? i18nPackage.t("editQuizPage.confirmationBlockTSX.supportedBrowsers")
									: i18nPackage.t("editQuizPage.confirmationBlockTSX.supportedPlatforms")
							}
						>
							<List disablePadding>
								{configs.map(({ name, Icon }) => (
									<DropdownRow
										key={name}
										icon={<Icon aria-hidden="true" style={{ width: "20px", height: "20px" }} />}
										text={name}
										listStyle={false}
									/>
								))}
								{isMobile && (
									<DropdownRow
										icon={<MobileIcon aria-hidden="true" />}
										text={getProctoringPlatformName("mobile", true)}
										listStyle={false}
									/>
								)}
								{isChromeOs && (
									<DropdownRow
										icon={
											lockdownRequireManagedGuestSession ? (
												<ManagedGuestSessionIcon aria-hidden="true" />
											) : (
												<PersonalChromebookIcon aria-hidden="true" />
											)
										}
										text={getProctoringPlatformName("chromeOs")}
										listStyle={false}
									/>
								)}
							</List>
						</CorePopoverButton>
					</>
				}
				titleTooltipType="wrap"
			/>
		</ConfirmationBlock>
	);
}

type ProfileBlockProps = {
	profile: ProctoringOptionProfileV2;
} & CommonConfirmationBlockProps;

export function ProfileBlock({ profile, ...props }: ProfileBlockProps) {
	const profileName = getProfileName(profile);

	return (
		<ConfirmationBlock
			{...props}
			title={i18nPackage.t("editQuizPage.confirmationBlockTSX.profileDetails")}
			targetPageName="profile"
		>
			<ConfirmationRow description={profileName} tooltipType={"overflow"} />
		</ConfirmationBlock>
	);
}

type LiveSessionBlockProps = CommonConfirmationBlockProps & {
	pendingLiveSessions: NonNullable<EditQuizSummaryProps["pendingLiveSessions"]>;
};

export function LiveSessionBlock({ pendingLiveSessions, loading, ...props }: LiveSessionBlockProps) {
	if (loading) return <ConfirmationBlockSkeleton />;

	return (
		<ConfirmationBlock
			{...props}
			title={i18nPackage.t("editQuizPage.confirmationBlockTSX.liveSession")}
			targetPageName="liveSessions"
			error={pendingLiveSessions.length === 0}
		>
			<ConfirmationRow
				description={i18nPackage.t("editQuizPage.confirmationBlockTSX.numSession", {
					count: pendingLiveSessions.length,
				})}
			/>
		</ConfirmationBlock>
	);
}

export type ConfigurationBlockProps = Omit<CommonConfirmationBlockProps, "onEdit"> &
	Pick<ConfigurationSummaryListProps, "onEdit" | "allowedCategories" | "showCategoriesWithNoItems"> &
	Partial<CategorizeProctoringOptionsParams>;

export function ConfigurationBlock({
	onEdit,
	applications,
	extensions,
	option,
	optionRelatedInstituteSettings,
	loading,
	verityInstituteSettings,
	allowedCategories,
	quizType,
	isThirdPartyEnabled,
	showCategoriesWithNoItems,
	referenceMaterialFiles,
	...props
}: ConfigurationBlockProps) {
	const summaries = useMemo(() => {
		if (!option) return null;
		return categorizeProctoringOptions({
			option,
			verityInstituteSettings,
			optionRelatedInstituteSettings,
			applications,
			extensions,
			quizType,
			isThirdPartyEnabled,
			referenceMaterialFiles,
		});
	}, [
		option,
		verityInstituteSettings,
		optionRelatedInstituteSettings,
		applications,
		extensions,
		quizType,
		isThirdPartyEnabled,
		referenceMaterialFiles,
	]);

	if (!summaries || loading) {
		return <ConfirmationBlockSkeleton />;
	}

	return (
		<ConfirmationBlock {...props} targetPageName="configuration">
			<ConfigurationSummaryList
				summary={summaries}
				onEdit={onEdit}
				allowedCategories={allowedCategories}
				showCategoriesWithNoItems={showCategoriesWithNoItems}
			/>
		</ConfirmationBlock>
	);
}
